import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["language"];
import { BASE_SERVICE_URI } from '../../constants/ApiConstants';
import { quickFetchOrGet } from '../../utils/ajaxUtils';
import http from 'hub-http/clients/apiClient';
import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import getLang from 'academy-common-lib/utils/getLang';
import { rpcClient } from '../../client';
import { getMLRecommendationsRpc, postRecommendationsFeedbackRpc, postRecommendationsContentEngagedRpc } from 'academy-service-client/academy-recommendations/v2/operations';
import { postV1RecommendationsFeedbackRpc } from 'academy-service-client/academy-recommendations/v1/operations';
import { useCallback } from 'react';
import Raven from 'raven-js';
import { useMlRecommendationsState } from './recommendationsStore';
export const GET_RECOMMENDATIONS = registerQuery({
  fieldName: 'recommendations',
  args: ['language'],
  fetcher({
    language
  }) {
    return quickFetchOrGet('recommendations', `${BASE_SERVICE_URI}/recommendations/v1/control/homepage/${language}`);
  }
});
export const getRecommendations = (language = getLang().toUpperCase()) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_RECOMMENDATIONS,
    variables: {
      language
    }
  });
};
export const useRecommendations = (language = getLang().toUpperCase()) => {
  const {
    data: recommendations,
    loading: recommendationsLoading,
    error: recommendationsError
  } = useQuery(GET_RECOMMENDATIONS, {
    variables: {
      language
    }
  });
  return {
    recommendations,
    recommendationsLoading,
    recommendationsError
  };
};
export const GET_RECOMMENDATIONS_TOPIC_PREFERENCE = registerQuery({
  fieldName: 'recommendationsTopicPreference',
  args: [],
  fetcher() {
    return quickFetchOrGet('topic-preferences', `${BASE_SERVICE_URI}/recommendations/v1/topic-preferences`);
  }
});
export const getRecommendationsTopicPreference = () => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_RECOMMENDATIONS_TOPIC_PREFERENCE
  });
};
export const useRecommendationsTopicPreference = () => {
  const {
    data: recommendationsTopicPreference,
    loading: recommendationsTopicPreferenceLoading,
    error: recommendationsTopicPreferenceError
  } = useQuery(GET_RECOMMENDATIONS_TOPIC_PREFERENCE);
  return {
    recommendationsTopicPreference,
    recommendationsTopicPreferenceLoading,
    recommendationsTopicPreferenceError
  };
};
export const GET_RECOMMENDATIONS_FOR_TRACK = registerQuery({
  fieldName: 'recommendationsForTrack',
  args: ['trackId'],
  fetcher({
    trackId
  }) {
    return http.get(`${BASE_SERVICE_URI}/recommendations/v1/track/${trackId}`);
  }
});
export const useRecommendationsForTrack = trackId => {
  const {
    data: recommendationsForTrack,
    loading: recommendationsForTrackLoading,
    error: recommendationsForTrackError
  } = useQuery(GET_RECOMMENDATIONS_FOR_TRACK, {
    variables: {
      trackId
    }
  });
  return {
    recommendationsForTrack,
    recommendationsForTrackLoading,
    recommendationsForTrackError
  };
};
export const GET_RECOMMENDATIONS_FOR_LESSON = registerQuery({
  fieldName: 'recommendationsForLesson',
  args: ['lessonId'],
  fetcher({
    lessonId
  }) {
    return http.get(`${BASE_SERVICE_URI}/recommendations/v1/lesson/${lessonId}`);
  }
});
export const useRecommendationsForLesson = lessonId => {
  const {
    data: recommendationsForLesson,
    loading: recommendationsForLessonLoading,
    error: recommendationsForLessonError
  } = useQuery(GET_RECOMMENDATIONS_FOR_LESSON, {
    variables: {
      lessonId
    }
  });
  return {
    recommendationsForLesson,
    recommendationsForLessonLoading,
    recommendationsForLessonError
  };
};
export const GET_ML_RECOMMENDATIONS = registerQuery({
  fieldName: 'MlRecommendations',
  args: ['language', 'size'],
  fetcher(_ref) {
    let {
        language
      } = _ref,
      queryParameters = _objectWithoutPropertiesLoose(_ref, _excluded);
    return rpcClient.call(getMLRecommendationsRpc, {
      queryParameters,
      pathParameters: {
        language
      }
    });
  }
});
export const useMlRecommendations = ({
  skip,
  language = getLang().toUpperCase(),
  size = 5
}) => {
  const {
    data: MlRecommendationsResponse,
    loading: MlRecommendationsLoading,
    error: MlRecommendationsError
  } = useQuery(GET_ML_RECOMMENDATIONS, {
    skip,
    variables: {
      language,
      size
    }
  });
  const explanations = MlRecommendationsResponse === null || MlRecommendationsResponse === void 0 ? void 0 : MlRecommendationsResponse.explanations;
  return {
    MlRecommendations: MlRecommendationsResponse === null || MlRecommendationsResponse === void 0 ? void 0 : MlRecommendationsResponse.results,
    correlationId: MlRecommendationsResponse === null || MlRecommendationsResponse === void 0 ? void 0 : MlRecommendationsResponse.correlationId,
    MlRecommendationsLoading,
    MlRecommendationsError,
    recommendationStrategy: explanations ? explanations['modelUsed'] === 'scann' || explanations['numCompletedContents'] === 0 ? 'cold' : 'warm' : undefined
  };
};
export const POST_ML_RECOMMENDATIONS_FEEDBACK = registerMutation({
  fieldName: 'MlRecommendationsFeedback',
  args: ['correlationId', 'entityId', 'entityType'],
  fetcher(data) {
    return rpcClient.call(postRecommendationsFeedbackRpc, {
      data
    });
  }
});
export const usePostMlRecommendationsFeedback = () => {
  const [mutate, {
    error: postMlRecommendationsFeedbackError,
    loading: postMlRecommendationsFeedbackLoading
  }] = useMutation(POST_ML_RECOMMENDATIONS_FEEDBACK);
  const postMlRecommendationsFeedback = useCallback(variables => {
    return mutate({
      variables,
      onError(error) {
        Raven.captureException(error);
      }
    });
  }, [mutate]);
  return {
    postMlRecommendationsFeedback,
    postMlRecommendationsFeedbackError,
    postMlRecommendationsFeedbackLoading
  };
};
export const POST_ML_RECOMMENDATIONS_CONTENT_ENGAGED_FEEDBACK = registerMutation({
  fieldName: 'MlRecommendationsContentEngagedFeedback',
  args: ['correlationId', 'entityId', 'entityType', 'engagedContentId'],
  fetcher(data) {
    return rpcClient.call(postRecommendationsContentEngagedRpc, {
      data
    });
  }
});
export const usePostMlRecommendationsContentEngagedFeedback = () => {
  const {
    clearLastClickedRecommendationItem,
    lastClickedRecommendationItem
  } = useMlRecommendationsState();
  const [mutate, {
    error: postMlRecommendationsContentEngagedFeedbackError,
    loading: postMlRecommendationsContentEngagedFeedbackLoading
  }] = useMutation(POST_ML_RECOMMENDATIONS_CONTENT_ENGAGED_FEEDBACK);
  const postMlRecommendationsContentEngagedFeedback = useCallback(({
    item,
    contentId
  }) => {
    // do not send feedback if there is no last clicked recommendation item or if it doesn't match the current item
    if (!lastClickedRecommendationItem || lastClickedRecommendationItem.item.id !== item.id || lastClickedRecommendationItem.item.type !== item.type) return;
    return mutate({
      variables: {
        correlationId: lastClickedRecommendationItem.correlationId,
        entityId: item.id,
        entityType: item.type,
        engagedContentId: contentId
      },
      onError(error) {
        Raven.captureException(error);
      },
      onCompleted() {
        clearLastClickedRecommendationItem();
      }
    });
  }, [clearLastClickedRecommendationItem, lastClickedRecommendationItem, mutate]);
  return {
    postMlRecommendationsContentEngagedFeedback,
    postMlRecommendationsContentEngagedFeedbackError,
    postMlRecommendationsContentEngagedFeedbackLoading
  };
};
export const POST_RECOMMENDATIONS_FEEDBACK = registerMutation({
  fieldName: 'RecommendationsFeedback',
  args: ['correlationId', 'entityId', 'entityType'],
  fetcher(data) {
    return rpcClient.call(postV1RecommendationsFeedbackRpc, {
      data
    });
  }
});
export const usePostRecommendationsFeedback = () => {
  const [mutate, {
    error: postRecommendationsFeedbackError,
    loading: postRecommendationsFeedbackLoading
  }] = useMutation(POST_RECOMMENDATIONS_FEEDBACK);
  const postRecommendationsFeedback = useCallback(variables => {
    return mutate({
      variables,
      onError(error) {
        Raven.captureException(error);
      }
    });
  }, [mutate]);
  return {
    postRecommendationsFeedback,
    postRecommendationsFeedbackError,
    postRecommendationsFeedbackLoading
  };
};